import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useAuthConfig } from '../contexts/AuthConfigContext';
import { useSiteConfig } from '../contexts/SiteConfigContext';
import { useLanguage } from '../contexts/LanguageContext';
import { useTranslation } from '../utils/translations';
import { appendSelectedPartnerToPath, getSelectedPartner, hasSelectedPartnerIdentity, hasVerifiedPartnerSelection } from '../utils/partnerSelection';
import { isDrawClosed as checkDrawClosed } from '../utils/drawLifecycle';

/**
 * Hook that gates purchase-route navigation behind auth.
 *
 * If the user is logged in (or guest checkout is enabled), navigates normally
 * with a view transition.  Otherwise opens the login modal and stores the
 * intended route so handleLoginSuccess can redirect after auth completes.
 *
 * @param {Function} onLoginClick – callback that opens the login modal
 *                                  (passed down from App → GameHomePage → layout)
 */
const HERO_PARTNER_REQUIRED_KEY = 'partnerRequiredOnHero';

function isPartnerRequiredOnHero() {
  try {
    return sessionStorage.getItem(HERO_PARTNER_REQUIRED_KEY) === 'true';
  } catch {
    return false;
  }
}

export function usePurchaseNavigate(onLoginClick) {
  const navigate = useNavigate();
  const { isLoggedIn } = useAuth();
  const { isGuestCheckoutEnabled } = useAuthConfig();
  const { siteDetails, nextDraw, isDrawClosed, loading } = useSiteConfig();
  const { language } = useLanguage();
  const { t } = useTranslation(language);

  return useCallback(
    (purchaseRoute) => {
      // If the draw is closed, block navigation to purchase/checkout and redirect to /closed
      const drawClosed = (!loading && isDrawClosed) || checkDrawClosed({ nextDraw, siteDetails });
      if (drawClosed) {
        navigate('/closed');
        return;
      }

      // If the partner field is configured as required on the homepage hero,
      // block navigation to purchase routes until a valid partner has been selected.
      const currentPartner = getSelectedPartner();
      if (isPartnerRequiredOnHero() && (!hasSelectedPartnerIdentity(currentPartner) || !hasVerifiedPartnerSelection(currentPartner))) {
        try {
          window.dispatchEvent(new CustomEvent('partner-required-focus'));
          window.dispatchEvent(new CustomEvent('partner-required-flash'));
        } catch {
          // ignore
        }
        return;
      }

      const routeWithPartner = appendSelectedPartnerToPath(purchaseRoute);

      if (isLoggedIn || isGuestCheckoutEnabled()) {
        navigate(routeWithPartner);
      } else {
        // Remember where the user wanted to go
        try {
          sessionStorage.setItem('pendingPurchaseRoute', routeWithPartner);
        } catch {
          // sessionStorage may be unavailable (private browsing, etc.)
        }
        // Open login modal directly — no navigation, no flash
        if (onLoginClick) onLoginClick();
      }
    },
    [navigate, isLoggedIn, isGuestCheckoutEnabled, onLoginClick, t, loading, isDrawClosed, nextDraw, siteDetails],
  );
}
